import { useRef, useState, useMemo } from "react";
import { Text, Reflector, Edges } from "@react-three/drei";
import { timelineData } from "../../model_data/timeline_data";
import { RigidBody } from "@react-three/rapier";
import { useFrame } from "@react-three/fiber";
import { useModal } from "../../contexts/ModalContext";
import GrowingUp from "./GrowingUp";
import HighSchool from "./HighSchool";
import College from "./College";
import { useTheme } from "../../contexts/ThemeContext";

export default function TimelinePortal({
    isVisible,
    currentIndex,
    onIndexChange,
}) {
    const portalRef = useRef();
    const groupRef = useRef();
    const [hoveredPortal, setHoveredPortal] = useState(null);
    const [hoveredArrow, setHoveredArrow] = useState(null);
    const [targetRotation, setTargetRotation] = useState(() => {
        const anglePerPortal = (Math.PI * 2) / timelineData.length;
        return -currentIndex * anglePerPortal + Math.PI;
    });
    const [currentRotation, setCurrentRotation] = useState(() => {
        const anglePerPortal = (Math.PI * 2) / timelineData.length;
        return -currentIndex * anglePerPortal + Math.PI;
    });
    const { openModal } = useModal();
    const { theme } = useTheme();

    const currentScene = useMemo(() => {
        return timelineData[currentIndex] || timelineData[0];
    }, [currentIndex]);

    // Circle configs
    const circleRadius = 8;
    const totalPortals = timelineData.length;

    const handlePortalClick = (index, isCurrentPortal) => {
        onIndexChange(index);
        updateRotationToIndex(index);

        if(isCurrentPortal) {
            switch(currentIndex){
                case 0: 
                    // Growing Up
                    openModal(<GrowingUp />, "");
                    break;
                case 1: 
                    // High School
                    openModal(<HighSchool />, "");
                    break;
                case 2:
                    // College
                    openModal(<College />, "");
                    break;
            }
        }
    };

    const handlePrevious = () => {
        const newIndex = currentIndex > 0 ? currentIndex - 1 : timelineData.length - 1;
        onIndexChange(newIndex);
        updateRotationToIndex(newIndex);
    };

    const handleNext = () => {
        const newIndex = currentIndex < timelineData.length - 1 ? currentIndex + 1 : 0;
        onIndexChange(newIndex);
        updateRotationToIndex(newIndex);
    };

    const updateRotationToIndex = (index) => {
        // bring the selected portal to the front
        const anglePerPortal = (Math.PI * 2) / totalPortals;
        const newTargetRotation = -index * anglePerPortal + Math.PI;
        setTargetRotation(newTargetRotation);
    };

    // Smooth rotation animation
    useFrame((state, delta) => {
        if (groupRef.current) {
            const rotationDiff = targetRotation - currentRotation;
            
            // Handle wrap-around for shortest path
            let adjustedDiff = rotationDiff;
            if (Math.abs(rotationDiff) > Math.PI) {
                if (rotationDiff > 0) {
                    adjustedDiff = rotationDiff - Math.PI * 2;
                } else {
                    adjustedDiff = rotationDiff + Math.PI * 2;
                }
            }
            
            const newRotation = currentRotation + adjustedDiff * delta * 5; // 5 is the animation speed
            setCurrentRotation(newRotation);
            groupRef.current.rotation.y = newRotation;
        }
    });

    // portal positions in a circle
    const getPortalPosition = (index) => {
        const anglePerPortal = (Math.PI * 2) / totalPortals;
        const angle = index * anglePerPortal;
        
        const x = Math.sin(angle) * circleRadius;
        const z = Math.cos(angle) * circleRadius;
        
        return [x, 1.5, z];
    };

    // portals face inward
    const getPortalRotation = (index) => {
        const anglePerPortal = (Math.PI * 2) / totalPortals;
        const angle = index * anglePerPortal;
        return [0, angle, 0];
    };

    if (!isVisible) return null;

    return (
        <group
            visible={true}
            rotation={[0, Math.PI / 2, 0]}
            position={[-45, -6, 15]}
        >
            {/* Reflective Platform */}
            <RigidBody type="fixed" position={[0, -2.3, 0.5]}>
                <mesh>
                    <Reflector
                        args={[25, 25]} 
                        resolution={256}
                        mirror={1} 
                        mixBlur={0}
                        mixStrength={1.4}
                        position={[0, 0, 0]}
                        rotation={[-Math.PI / 2, 0, 0]}
                    />
                </mesh>
            </RigidBody>

            {/* Rotating group containing all portals */}
            <group ref={groupRef}>
                {/* Timeline portals */}
                {timelineData.map((sceneData, index) => {
                    const position = getPortalPosition(index);
                    const rotation = getPortalRotation(index);
                    const isCurrentPortal = index === currentIndex;

                    return (
                        <group key={index} position={position} rotation={rotation}>
                            <mesh
                                ref={isCurrentPortal ? portalRef : null}
                                onClick={() => handlePortalClick(index, isCurrentPortal)}
                                onPointerEnter={() => setHoveredPortal(index)}
                                onPointerLeave={() => setHoveredPortal(null)}
                                scale={
                                    hoveredPortal === index ? 1.1 : isCurrentPortal ? 1 : 0.9
                                }
								rotation={[0, Math.PI, 0]}
                            >
                                <planeGeometry args={[4, 3]} />
                                <meshBasicMaterial
                                    color={theme === "dark" ? "#000020" : "#002000"}
                                    transparent
                                    opacity={isCurrentPortal ? 0.9 : 0.6}
                                />
                                <Edges color="#00f2ff" />
                            </mesh>

                            {/* Timeline label - show for all portals but highlight current */}
                            <Text
                                position={[0, -2, 0]}
								rotation={[0, Math.PI, 0]}
                                fontWeight={800}
                                fontSize={isCurrentPortal ? 0.4 : 0.25}
                                color={theme === 'dark' ? "#00f2ff" : "black"}
                                anchorX="center"
                                anchorY="middle"
                                maxWidth={3}
                            >
                                {sceneData.title}
                            </Text>
                        </group>
                    );
                })}
            </group>

            {/* Static UI elements that don't rotate */}
            <group>
                {/* Navigation UI */}
                {/* Left Arrow */}
                <mesh
                    position={[-3.5, 1.7, -8]}
                    rotation={[0, 0, Math.PI / 2]}
                    onClick={handlePrevious}
                    onPointerEnter={() => setHoveredArrow("left")}
                    onPointerLeave={() => setHoveredArrow(null)}
                    scale={hoveredArrow === "left" ? 1.7 : 1.5}
                >
                    <coneGeometry args={[0.3, 0.8, 3]} />
                    <meshBasicMaterial
                        color={hoveredArrow === "left" ? "#00f2ff" : "white"}
                    />
                </mesh>

                {/* Right Arrow */}
                <mesh
                    position={[3.5, 1.7, -8]}
                    rotation={[0, 0, -Math.PI / 2]}
                    onClick={handleNext}
                    onPointerEnter={() => setHoveredArrow("right")}
                    onPointerLeave={() => setHoveredArrow(null)}
                    scale={hoveredArrow === "right" ? 1.7 : 1.5}
                >
                    <coneGeometry args={[0.3, 0.8, 3]} />
                    <meshBasicMaterial
                        color={hoveredArrow === "right" ? "#00f2ff" : "white"}
                    />
                </mesh>

                {/* Center indicator - shows player position */}
                <mesh position={[0, -2.34, 0]}>
                    <cylinderGeometry args={[0.5, 0.5, 0.1, 8]} />
                    <meshBasicMaterial color="#00f2ff" transparent wireframe={true} opacity={0.3} />
                </mesh>
            </group>
        </group>
    );
}